import * as React from 'react';

import {ChatBubbleRegularIcon} from 'shells/icon/svg/chat-bubble-regular-icon';
import {TimelineComments} from 'shells/timeline/entry/comments/timeline-comments';
import type {Reactions} from 'shells/timeline/reaction-bar/types';

import type {ReactionType} from 'nutshell-graphql-types';
import type {
    GetActivityForModal_activity as Activity,
    GetSessionUser_loggedInUser as User,
} from 'nutshell-graphql-types';

import {getCommentsWithFilteredReactions} from '../../../timeline/reactions/helpers';

import {useCreateComment, useDeleteComment} from '../../graphql/hooks';
import {useToggleReaction} from '../../graphql/hooks/use-toggle-reaction';
import {refreshActivityState} from '../../helpers';

import {EmptyTab} from '../empty-tab';

import styles from './comments.module.css';

type Props = {
    activity: Activity;
    currentUser: User;
};

export function Comments(props: Props) {
    const createComment = useCreateComment();
    const deleteComment = useDeleteComment();
    const toggleReaction = useToggleReaction();

    // Filter out comments with no valid ID (e.g. deleted comments)
    const comments = props.activity.comments.edges
        .map((comment) => comment.node)
        .filter((comment) => comment.id);

    const onAddComment = (value: string) => {
        return createComment({
            activityId: props.activity.id,
            value,
            user: props.currentUser,
            // @ts-expect-error
        }).then(refreshActivityState(true, props.activity.id));
    };

    const onDeleteComment = (commentId: string) => {
        return deleteComment({
            activityId: props.activity.id,
            commentId: commentId,
            // @ts-expect-error
        }).then(refreshActivityState(true, props.activity.id));
    };

    const onToggleReaction = (commentId: string, reaction: ReactionType, reactions: Reactions) => {
        return toggleReaction({
            changeLogId: commentId,
            reactionType: reaction,
            reactions: reactions,
            user: props.currentUser,
            activityId: props.activity.id,
            // @ts-expect-error
        }).then(refreshActivityState(true, props.activity.id));
    };

    return (
        <div className={styles['comments-tab-container']}>
            <div className='px-16 pt-16'>
                <div className={styles['comments-container']}>
                    <TimelineComments
                        comments={getCommentsWithFilteredReactions(comments)}
                        currentUserId={props.currentUser.id}
                        onAddComment={onAddComment}
                        onDeleteComment={onDeleteComment}
                        isAddingComment={true}
                        onToggleReaction={onToggleReaction}
                    />
                </div>
            </div>
            {comments.length === 0 && (
                <div className={styles['empty-tab-container']}>
                    <EmptyTab
                        title='No comments'
                        subtitle='Add a comment to start a conversation, ask a question, or offer guidance.'
                        icon={ChatBubbleRegularIcon}
                    />
                </div>
            )}
        </div>
    );
}
